import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    Watch,
} from "@stencil/core";
import api from "../../utils/api";

@Component({
    tag: "ux-iws-async-html",
    styleUrl: "async-html.scss",
    shadow: false,
})
export class AsyncHtml {
    @Element() domNode: HTMLUxIwsAsyncHtmlElement;

    /** url of the async content */
    @Prop() readonly src: string | null = null;
    /** make debugging available*/
    @Prop() readonly debug: boolean = false;
    /** if set, the html content received as response will replace the component itself */
    @Prop() readonly replace: boolean = false;

    /** event emitted when the fetch is successful and the content is ready to be inserted */
    @Event({
        eventName: "uxIwsAsyncHtmlInsertReady",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    asyncHtmlInsertReady: EventEmitter<{
        willReplaceNode: boolean;
    }>;

    /** event emitted when the loaded content is inserted in the element */
    @Event({
        eventName: "uxIwsAsyncHtmlContentInserted",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    asyncHtmlContentInserted: EventEmitter<boolean>;

    async componentWillLoad() {
        if (this.src != null) {
            this.getContent();
        }
    }

    @Watch("src")
    loadContent() {
        this.getContent();
    }

    private _log = (msg: string) => {
        if (this.debug) {
            console.log(`[ASYNC-HTML]: ${msg}`);
        }
    };

    private getContent = async () => {
        try {
            if (this.src == null || this.src === "") {
                throw new Error("No source submitted");
            }

            if (isMockUrl(this.src)) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }

            const responseHTML = await api<string>(this.src);
            if (this.replace) {
                this.replaceComponent(responseHTML);
            } else if (responseHTML.length > 0) {
                this.insertInComponent(responseHTML);
            } else {
                this.asyncHtmlContentInserted.emit(false);
            }
        } catch (e) {
            console.log("Could not fetch async html", e);
            this.asyncHtmlContentInserted.emit(false);
        }
    };

    private dispatchReadyEvent = () => {
        const insertReadyEvent = this.asyncHtmlInsertReady.emit({
            willReplaceNode: this.replace,
        });
        return insertReadyEvent;
    };

    private insertScript = (response: string) => {
        const scriptTags = response.match(
            /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi
        );
        if (scriptTags) {
            scriptTags.forEach((scriptTag) => {
                const attributes = scriptTag.match(/(\w+)="(.*?)"/g);
                if (attributes) {
                    const script = document.createElement("script");
                    attributes.forEach((attribute) => {
                        const [key, value] = attribute.split("=");
                        script.setAttribute(key, value.replace(/"/g, ""));
                    });
                    document.body.appendChild(script);
                    this._log("Script injected: " + script);
                }
            });
        } else {
            this._log("No script to inject");
        }
        return response;
    };

    private insertInComponent = (responseHTML: string) => {
        this._log("Insert HTML");
        const readyEvent = this.dispatchReadyEvent();
        if (!readyEvent.defaultPrevented) {
            this.insertScript(responseHTML);
            this.domNode.innerHTML = this.filterHTML(responseHTML);
            this.domNode.classList.add("content-loaded");
            this.asyncHtmlContentInserted.emit(true);
        } else {
            this.asyncHtmlContentInserted.emit(false);
        }
    };

    private replaceComponent = (responseHTML: string) => {
        this._log("Replacing Component");
        const readyEvent = this.dispatchReadyEvent();
        if (!readyEvent.defaultPrevented) {
            this.domNode.insertAdjacentHTML(
                "beforebegin",
                this.filterHTML(responseHTML)
            );
            this.insertScript(responseHTML);
            this.asyncHtmlContentInserted.emit(true);
            this.domNode.remove();
        } else {
            this.asyncHtmlContentInserted.emit(false);
        }
    };

    private filterHTML = (html: string) => {
        const scriptTags = html.match(
            /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi
        );
        if (scriptTags) {
            scriptTags.forEach((scriptTag) => {
                html = html.replace(scriptTag, "");
            });
        }
        return html;
    };

    render() {
        return <Host></Host>;
    }
}

function isMockUrl(url: string) {
    return url.includes("/mock-data/");
}
